import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, SimpleDialog, Typography, TextField, Button, makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useTheme } from '@mui/material';
import { useIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import useApplicationForm from './useApplicationForm';
import { v4 as uuid } from 'uuid';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { useSelector, useDispatch } from 'react-redux';
import { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice';
import {
    borderWidthThin,
    borderRadiusMedium,
    colorBackgroundDefault,
    colorGlobalBorderDefault,
    fontWeightLight,
    spacing40,
    spacing30,
    spacing50,
    fontSizeSmall
} from '@ellucian/react-design-system/core/styles/tokens';

const useStyles = makeStyles(theme => ({
    content: {
        padding: spacing50,
        display: 'grid',
        gridAutoRows: 'min-content',
    },
    pageDivider: {
        marginBottom: [0, '!important'],
    },
    feeItems: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
    },
    feeItemCell: {
        padding: spacing30,
    },
    rightAlign: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: spacing40,
    },
    total: {
        backgroundColor: colorBackgroundDefault,
        borderBottom: `${colorGlobalBorderDefault} ${borderWidthThin} solid`,
    },
    feeDiscount: {
        border: `${borderWidthThin} solid ${colorGlobalBorderDefault}`,
        borderRadius: borderRadiusMedium,
        marginTop: spacing50,
    },
    input: {
        justifyContent: 'flex-start',
    },
    buttons: {
        marginLeft: spacing30,
    },
    discountDescription: {
        fontWeight: fontWeightLight,
        fontSize: fontSizeSmall
    },
    endAlign: {
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: spacing40,
        padding: spacing40
    },
    [theme.breakpoints.down('sm')]: {
        endAlign: {
            gridTemplateColumns: '1fr'
        },
    },
    [theme.breakpoints.up('lg')]: {
        content: {
            width: '50%',
        },
    },
}));

const selectAllErrors = state => state.form.errors;
const selectApplicationId = state => state.application.id;
const selectApplicationStatus = state => state.application.status;
const selectApplicationPayment = state => state.application.paymentInformation;

const ApplicationPayments = ({ applicationFee }) => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const snackbar = useSnackbar();
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { applicantPaymentQueryParam, discountType} = CONSTANTS;
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const hasParams = useMemo(() => params?.get(applicantPaymentQueryParam.key) === applicantPaymentQueryParam.value, [params]);
    const { previewMode, externalMode, template, adminTenant, savePayment, validateDiscountCode } = useApplicationForm();
    const errors = useSelector(selectAllErrors);
    const applicationId = useSelector(selectApplicationId);
    const applicationStatus = useSelector(selectApplicationStatus);
    const applicationPayment = useSelector(selectApplicationPayment);
    const globalSettings = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const currency = globalSettings?.paymentSettings?.currency;
    const [showFailedPayment, setShowFailedPayment] = useState(false);
    const [showApplyDiscount, setShowApplyDiscount] = useState(true);
    const [discountCodeState, setDiscountCodeState] = useState({ value: applicationPayment?.appliedDiscountCode });
    const initialDiscountValue = useMemo(() => applicationPayment?.appliedDiscountValue === undefined ? 0 : applicationPayment.appliedDiscountValue, [applicationPayment?.appliedDiscountValue]);
    const [hasResetPayment, setHasResetPayment] = useState(false);
    const [discountValue, setDiscountValue] = useState(initialDiscountValue);

    const resetPayment = useCallback(() => {
        if (applicationFee) {
            savePayment({
                transactionId: uuid(),
                paymentProvider: globalSettings.paymentSettings.paymentProvider,
                amount: applicationFee.amount,
                currency,
                paymentStatus: CONSTANTS.applicationPaymentInformation.paymentStatus.draft,
            });
            setHasResetPayment(true);
        }
    }, [applicationFee?.amount, currency, globalSettings.paymentSettings.paymentProvider]);

    useEffect(() => {
        if (!hasResetPayment && applicationFee && (!applicationPayment || applicationFee?.amount !== applicationPayment?.amount)) {
            resetPayment();
        }
    }, [applicationFee, applicationPayment, resetPayment]);

    useEffect(() => {
        const reviewRoute = previewMode ? routes.adminTemplatePreviewReviewPage : externalMode ? routes.externalApplicationwReviewPage : routes.applicationFormReviewPage;
        const submittedRoute = previewMode ? routes.adminTemplatePreviewSubmitPage : externalMode ? routes.externalApplicationSubmitPage : routes.applicationFormSubmitPage;
        const suppItemsRoute = previewMode ? routes.adminTemplatePreviewSupplementalItemsPage : externalMode ? routes.externalApplicationSupplementalItemsPage : routes.applicationFormSupplementalItemsPage;
        const paymentsRoute = externalMode ? routes.externalApplicationPayments : routes.applicationFormPayments;

        if (applicationStatus === CONSTANTS.applicationFormStatuses.SUBMITTED) {
            if (template?.settings?.enableSupplementalItems && template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0) {
                history.push({
                    pathname: generatePath(suppItemsRoute, { id: applicationId }),
                    state: hasParams ? { showDialog: true } : undefined,
                });
            } else {
                history.push({
                    pathname: generatePath(submittedRoute, { id: applicationId }),
                    state: hasParams ? { showDialog: true } : undefined,
                });
            }
        }
        else if (applicationStatus !== CONSTANTS.applicationFormStatuses.STARTED) {
            history.push(routes.home);
        }
        else if (Object.keys(errors).length > 0) {
            history.push(generatePath(reviewRoute, { id: applicationId }));
        }
        else if (applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.started) {
            if (hasParams) {
                history.push({
                    pathname: generatePath(reviewRoute, { id: applicationId }),
                    state: hasParams ? { showDialog: true } : undefined,
                });
            }
            else {
                // person left, create new payment
                resetPayment();
            }
        }
        else if (hasParams && applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.failed) {
            if (hasParams) {
                setShowFailedPayment(true);
                history.replace({
                    pathname: generatePath(paymentsRoute, { id: applicationId }),
                    state: hasParams ? { showDialog: true } : undefined,
                    search: undefined,
                });
            }
        }
        if (applicationPayment?.appliedDiscountValue) {
            setShowApplyDiscount(false);
        }

    }, []);

    const closeDialog = useCallback(() => setShowFailedPayment(false), [setShowFailedPayment]);

    // handle alphanumeric on key pressed 
    const handleChange = useCallback(event => {
        let { value } = event?.target || {};
        value = value?.toUpperCase();
        const re = /^[a-zA-Z0-9]*$/;
        let error = '';
        if (re.test(value)) {
            setDiscountCodeState({ value, error });
        }
    }, []);

    // handle discount code on button click
    const handleDiscountApplyClick = useCallback(async () => {
        dispatch(startLoading());
        try {
            const value = discountCodeState.value;
            const valid = await validateDiscountCode(value);
            const error = valid ? '' : formatMessage({ id: 'applicationFeeDiscountCodeInvalid' });
            if (valid) {
                const discAmount = calculateDiscount(valid);
                setDiscountValue(discAmount);

                let newPayment = Object.assign({}, applicationPayment);
                if (!applicationPayment) {
                    newPayment = {
                        transactionId: uuid(),
                        paymentProvider: globalSettings.paymentSettings.paymentProvider,
                        amount: applicationFee.amount,
                        currency,
                        transactionType: CONSTANTS.applicationPaymentInformation.transactionTypes.sales,
                        paymentMethod: CONSTANTS.applicationPaymentInformation.paymentMethods.creditCard,
                        paymentStatus: CONSTANTS.applicationPaymentInformation.paymentStatus.draft,
                    };
                }
                newPayment.appliedDiscountCode = value;
                newPayment.appliedDiscountValue = discAmount;
                await savePayment(newPayment);
            }
            setDiscountCodeState(prevState => {
                return { ...prevState, error: error, description: valid?.description };
            });
            setShowApplyDiscount(!valid);

        }
        catch (e) {
            snackbar.error(formatMessage({ id: 'generalErrorMessage' }));
            console.error(e);
        }
        dispatch(stopLoading());
    }, [dispatch, startLoading, stopLoading, setDiscountCodeState, setDiscountValue, setShowApplyDiscount, discountCodeState, snackbar]);

    // calculate discount 
    const calculateDiscount = (discountObject) => {
        if (!discountObject) { return null; }
        if (discountObject?.discountType === discountType.amount) {
            return discountObject?.discountValue;
        }
        else {
            return applicationFee.amount * (discountObject?.discountValue / 100);
        }
    };
    const handleDiscountRemoveClick = useCallback(async () => {
        setDiscountValue(0);
        setDiscountCodeState({ value: '' });
        setShowApplyDiscount(true);
        // delete the discount code from db
        try {
            let newPayment = Object.assign({}, applicationPayment);
            newPayment.appliedDiscountCode = '';
            newPayment.appliedDiscountValue = 0;
            await savePayment(newPayment);
        }
        catch (e) {
            snackbar.error(formatMessage({ id: 'generalErrorMessage' }));
            console.error(e);
        }
    }, [setShowApplyDiscount, snackbar]);

    if (!applicationFee) {
        return null;
    }
    return (
        <div className={classes.content}>
            <Typography variant="h2">
                <FormattedMessage id="paymentPage" />
            </Typography>
            <Divider />
            <Typography variant="h3">
                <FormattedMessage id="applicationFees" />
            </Typography>
            <Divider />
            <div className={classes.feeItems}>
                <Typography className={classes.feeItemCell}>{applicationFee.name}</Typography>
                <Typography className={[classes.feeItemCell, classes.rightAlign].join(' ')}>
                    <FormattedNumber value={applicationFee.amount} style="currency" currency={currency} />
                </Typography>
            </div>
            <Divider className={classes.pageDivider} />
            <div className={classes.feeItems}>
                <Typography className={[classes.feeItemCell, classes.rightAlign].join(' ')}>
                    <FormattedMessage id="applicationFeeTotal" />
                </Typography>
                <Typography className={[classes.feeItemCell, classes.rightAlign].join(' ')}>
                    <FormattedNumber value={applicationFee.amount} style="currency" currency={currency} />
                </Typography>
            </div>
            <div className={classes.feeItems}>
                {!showApplyDiscount &&
                    <>
                        <Typography className={[classes.feeItemCell, classes.rightAlign].join(' ')}>
                            <FormattedMessage id="applicationFeeDiscount" />
                        </Typography>
                        <Typography className={[classes.feeItemCell, classes.rightAlign].join(' ')}>
                            <FormattedNumber value={Math.min(discountValue, applicationFee.amount)} style="currency" currency={currency} />
                        </Typography>
                    </>
                }

            </div>
            <div className={classes.feeItems}>
                <Typography className={[classes.feeItemCell, classes.rightAlign, classes.total].join(' ')}>
                    <strong><FormattedMessage id="applicationFeeSubtotal" /></strong>
                </Typography>
                <Typography className={[classes.feeItemCell, classes.rightAlign, classes.total].join(' ')}>
                    <strong><FormattedNumber value={discountValue >= applicationFee.amount ? 0 : (applicationFee.amount - discountValue)} style="currency" currency={currency} /></strong>
                </Typography>
            </div>
            <div className={classes.feeDiscount}>
                {showApplyDiscount &&
                    <div className={classes.endAlign}>
                        <TextField className={classes.input}
                            fullWidth="true"
                            label={formatMessage({ id: 'applicationDiscountLabel' })}
                            helperText={!discountCodeState.error ? formatMessage({ id: 'applicationDiscountHelperText' }) : discountCodeState.error}
                            disabled={applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending}
                            onChange={handleChange}
                            error={!!discountCodeState.error}
                            value={discountCodeState.value}
                        />
                        <Button className={classes.buttons}
                            variant="text"
                            onClick={handleDiscountApplyClick}
                            disabled={applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending}                            
                        >
                            <FormattedMessage id="applicationDiscountApplyButton" />
                        </Button>

                    </div>

                }
                {!showApplyDiscount &&
                    <div className={classes.endAlign}>
                        <div className={classes.input}>
                            <Typography >
                                <FormattedMessage id="applicationDiscountCodeApplied" values={{ code: discountCodeState.value }} />
                            </Typography>
                            <Typography className={classes.discountDescription}>
                                {discountCodeState?.description}
                            </Typography>
                        </div>
                        <Button className={classes.buttons}
                            variant="text"
                            onClick={handleDiscountRemoveClick}
                            disabled={applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending}
                        >
                            <FormattedMessage id="applicationDiscountRemoveButton" />
                        </Button>
                    </div>

                }

            </div>
            {showFailedPayment &&
                <SimpleDialog
                    buttonConfig={[
                        {
                            id: 'ok',
                            title: formatMessage({ id: 'dialogOk' })
                        },
                    ]}
                    title={formatMessage({ id: 'payFailureHeader' })}
                    open={showFailedPayment}
                    closeOnClickOut={false}
                    handleButtonClick={closeDialog}
                    showTitleBarClose={true}
                    onClose={closeDialog}
                    fullScreen={isMobile}
                >
                    <Typography><FormattedMessage id="payFailureContent" /></Typography>
                </SimpleDialog>
            }
        </div>
    );
};

ApplicationPayments.propTypes = {
    applicationFee: PropTypes.object,
};

export default ApplicationPayments;
